import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RevealLines, FadeUp } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";

const MANIFESTO = "We got tired of grey, black and beige. So we made socks that scream, wink and start conversations. Comfortable enough for all day. Loud enough for all night.";

const VALUES = [
  { n: "01", t: "Loud by default", d: "Every pair is designed to be noticed. Subtle is not in our vocabulary.", c: "text-lime" },
  { n: "02", t: "Comfort is law", d: "Funky never means scratchy. Soft yarns, snug cuffs, happy toes.", c: "text-pink" },
  { n: "03", t: "Made for the group chat", d: "Pairs worth a photo. Tag us, flex it, start a trend.", c: "text-cyan" },
];

const Word = ({ word, progress, range }) => {
  const opacity = useTransform(progress, range, [0.25, 1]);
  return <motion.span style={{ opacity }} className="mr-[0.25em] inline-block">{word}</motion.span>;
};

const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.45"] });
  const words = MANIFESTO.split(" ");
  return (
    <section ref={ref} data-testid="about-manifesto" className="px-5 py-24 md:px-10 md:py-40">
      <p className="mx-auto max-w-[1200px] font-display text-3xl font-bold leading-[1.1] tracking-tight md:text-6xl">
        {words.map((w, i) => <Word key={i} word={w} progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]} />)}
      </p>
    </section>
  );
};

const AboutHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  return (
    <section ref={ref} data-testid="about-hero" className="relative overflow-hidden px-5 pb-16 pt-28 md:px-10 md:pt-40">
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-pink">About Insane Circle</motion.p>
          <RevealLines testId="about-title" className="font-display text-[clamp(2.6rem,11vw,4.5rem)] lg:text-[5vw] 2xl:text-[6rem] font-extrabold uppercase leading-[0.86] tracking-[-0.045em]"
            lines={["Born bored ", "of boring ", <span key="s" className="text-lime">socks.</span>]} />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="mt-8 max-w-md text-base text-haze md:text-lg">
            Insane Circle is a sock brand for the insane generation—the insane people, born to do insane things on this INSANE Earth.
          </motion.p>
        </div>
        <motion.div style={{ y }} className="lg:col-span-5">
          <motion.div initial={{ clipPath: "inset(0% 100% 0% 0% round 32px)" }} animate={{ clipPath: "inset(0% 0% 0% 0% round 32px)" }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.5 }} className="relative aspect-[4/5] overflow-hidden rounded-[32px]">
            <img src="/img/burning-fire.jpg" alt="Burning Fire flame print socks" className="h-full w-full object-cover" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Values = () => (
  <section data-testid="about-values" className="px-5 py-24 md:px-10">
    <div className="mx-auto max-w-[1440px]">
      <FadeUp><h2 className="font-display text-4xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-5xl lg:text-7xl">What we<br />stand on</h2></FadeUp>
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {VALUES.map((v, i) => (
          <FadeUp key={v.n} delay={i * 0.1}>
            <motion.div whileHover={{ y: -8, rotate: i === 1 ? 1 : -1 }} data-testid={`value-card-${v.n}`} className="h-full rounded-[32px] border border-violet/50 bg-plum p-8">
              <span className={`font-display text-6xl font-extrabold ${v.c}`}>{v.n}</span>
              <p className="mt-10 font-display text-2xl font-extrabold uppercase">{v.t}</p>
              <p className="mt-3 text-base text-haze">{v.d}</p>
            </motion.div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Story = () => (
  <section data-testid="about-story" className="px-5 py-24 md:px-10">
    <div className="mx-auto grid max-w-[1440px] items-center gap-12 md:grid-cols-2">
      <FadeUp className="overflow-hidden rounded-[32px]">
        <img src="/img/insane-heroes.jpg" alt="Insane Heroes comic print socks" className="aspect-square w-full object-cover" />
      </FadeUp>
      <FadeUp delay={0.1}>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lime">Our story</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold uppercase leading-[0.95] sm:text-4xl lg:text-5xl">From one weird pair to a whole movement</h2>
        <p className="mt-6 text-base text-haze">Born from a crazy idea by a BITS Pilani alumnus. Insane Circle is made for the insane generation- the ones who think differently, dream bigger, and see art in everything.</p>
      </FadeUp>
    </div>
  </section>
);

export default function About() {
  useSeo({
    title: "About Insane Circle — Socks for the Insane Generation",
    description: "Insane Circle is a sock brand for the insane generation, born from a crazy idea by a BITS Pilani alumnus. Meet the brand behind Burning Fire and Insane Heroes.",
    path: "/about",
  });
  const navigate = useNavigate();
  return (
    <div data-testid="about-page">
      <AboutHero />
      <Manifesto />
      <Story />
      <Values />
      <section className="px-5 py-24 md:px-10 md:py-32">
        <FadeUp className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-8 rounded-[40px] bg-violet p-8 md:flex-row md:items-center md:p-16">
          <p className="font-display text-4xl font-extrabold uppercase leading-[0.9] md:text-7xl">Ready to<br />lose it?</p>
          <motion.button data-testid="about-shop-cta" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate("/#shop")}
            className="flex items-center gap-2 rounded-full bg-lime px-8 py-4 font-display text-lg font-extrabold uppercase text-ink">
            Shop now <ArrowUpRight className="h-5 w-5" />
          </motion.button>
        </FadeUp>
      </section>
    </div>
  );
}
